// Iter BC — set a new password from an emailed reset link.
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LockKeyhole } from "lucide-react";
import { api } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — sign in with your new password");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full min-h-[48px] px-4 rounded-2xl bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-ink-900";

  if (!token) {
    return (
      <div className="min-h-screen bg-paper-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="reset-invalid">
          <h1 className="font-heading text-2xl text-ink-900 mb-2">Missing reset token</h1>
          <p className="text-ink-500 text-sm mb-6">This link looks incomplete. Request a new reset email.</p>
          <Link to="/forgot-password" className="underline underline-offset-4 text-ink-700">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <div className="text-center mb-8">
          <span className="w-12 h-12 rounded-2xl bg-sage-100 text-sage-700 inline-flex items-center justify-center mb-4">
            <LockKeyhole className="w-5 h-5" strokeWidth={1.5} />
          </span>
          <h1 className="font-heading text-3xl text-ink-900 mb-2">Choose a new password</h1>
          <p className="text-ink-500 text-sm">Your reset link is valid for 1 hour and can only be used once.</p>
        </div>
        <form onSubmit={submit} className="space-y-3 bg-white border border-paper-200 rounded-3xl p-6 sm:p-8">
          <input data-testid="reset-password-input" required type="password" placeholder="New password (min 6 characters)" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          <input data-testid="reset-confirm-input" required type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} />
          <button data-testid="reset-submit-btn" type="submit" disabled={busy} className="w-full min-h-[52px] rounded-full bg-sage-500 hover:bg-sage-600 disabled:opacity-50 text-white font-medium transition-all">
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>
        <div className="text-center mt-6">
          <Link to="/login" className="text-sm text-ink-700 underline underline-offset-4">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
